import { useState } from "react";
import { useAppDispatch } from "redux/hooks";
import { LocalForageZoom, PresetDocumentZoom, setSavedZoom } from "redux/slices/session";

export interface UseZoomControls {
  scale: number;
  setScale: (scale: number) => void;
  savedZoom: LocalForageZoom;
  option: PresetDocumentZoom;
  setOption: (option: PresetDocumentZoom) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  magnified: boolean;
  onZoomSelect: (option: PresetDocumentZoom, scale: number) => void;
  shouldSaveZoom: boolean;
  setShouldSaveZoom: (should: boolean) => void;
}
export function useZoomControls(savedZoom: LocalForageZoom) {
  const dispatch = useAppDispatch();
  const [option, _setOption] = useState<PresetDocumentZoom>(savedZoom.option ?? "page-width");
  const [scale, _setScale] = useState(savedZoom.scale ?? 1);
  const [shouldSaveZoom, _setShouldSaveZoom] = useState(savedZoom.scale !== undefined);
  const [magnified, setMagnified] = useState<boolean>(false);

  const amountToZoomIn =
    scale <= 0.75
      ? 0.075
      : scale >= 0.75 && scale < 1.5
        ? 0.25
        : scale >= 1.5 && scale < 2.0
          ? 0.5
          : scale >= 2.0 && scale < 4.0
            ? 1.0
            : 4.0;

  const amountToZoomOut =
    scale >= 8.0
      ? 4.0
      : scale <= 4.0 && scale > 2.0
        ? 1.0
        : scale <= 2.0 && scale > 1.5
          ? 0.5
          : scale <= 1.5 && scale > 0.75
            ? 0.25
            : 0.075;
  const canZoomIn = scale < 8.0;
  const canZoomOut = scale > 0.1;

  const setScale = (s: number) => {
    _setScale(s);
    if (shouldSaveZoom) {
      dispatch(setSavedZoom({ scale: s, option }));
    }
  };

  const setOption = (o: PresetDocumentZoom) => {
    _setOption(o);
    if (shouldSaveZoom) {
      dispatch(setSavedZoom({ option: o, scale }));
    }
  };

  const _setBoth = (o: PresetDocumentZoom, s: number) => {
    _setOption(o);
    _setScale(s);
    if (shouldSaveZoom) {
      dispatch(setSavedZoom({ option: o, scale: s }));
    }
  };

  const zoomIn = () => {
    setScale(Math.min(8.0, scale + amountToZoomIn));
    setMagnified(true);
  };

  const zoomOut = () => {
    setScale(Math.max(0.1, scale - amountToZoomOut));
    setMagnified(true);
  };

  const onZoomSelect = (option: PresetDocumentZoom, scale: number) => {
    setMagnified(false);
    _setBoth(option, scale);
  };

  const setShouldSaveZoom = (should: boolean) => {
    _setShouldSaveZoom(should);
    dispatch(setSavedZoom(!should ? {} : { option, scale }));
  };

  return {
    shouldSaveZoom,
    setShouldSaveZoom,
    onZoomSelect,
    magnified,
    zoomIn,
    zoomOut,
    amountToZoomIn,
    amountToZoomOut,
    canZoomIn,
    canZoomOut,
    scale,
    setScale,
    savedZoom,
    option,
    setOption,
  };
}
